/**
 * Reading Google Calendar straight from the browser.
 *
 * There is no backend here to hold a refresh token (see the README: the whole
 * site is a static bundle and IAM), so the page holds a short-lived access
 * token in memory and talks to the Calendar REST API itself. The grant is
 * `calendar.readonly`, so no request this module could ever be made to send
 * can change anything: the guarantee is in the token, not in the code below.
 *
 * **Every calendar, not the primary one.** The desktop app read `primary`
 * alone and reported an empty day when the day was full, because the study
 * plan and the timetable live on calendars Alter creates. So this lists
 * `calendarList` first and then reads events from each one.
 *
 * **One calendar that fails does not sink the rest.** A shared calendar can be
 * revoked, deleted, or simply refuse; that returns as a named entry in
 * `failed` alongside everything that did answer. The one exception is a 401,
 * which is not that calendar's problem — the token has run out and every other
 * calendar is about to say the same — so it stops the whole load and asks for
 * a fresh consent.
 *
 * No DOM here. The network is a single injectable `fetchImpl`, the way
 * canvas.js does it.
 */
import { shapeEvent } from './calendar.js';

const API = 'https://www.googleapis.com/calendar/v3';
const PAGE_SIZE = '250';

/** The grant is gone. Only a fresh consent fixes this, and it is one click. */
export class GoogleAuthExpired extends Error {
  constructor(message) {
    super(message || 'Google access has expired.');
    this.name = 'GoogleAuthExpired';
    // What the UI keys off, the way CanvasExpired's `needsPC` does.
    this.needsConsent = true;
  }
}

export class GoogleUnavailable extends Error {
  constructor(message) {
    super(message || 'Could not reach Google Calendar.');
    this.name = 'GoogleUnavailable';
    this.needsConsent = false;
  }
}

async function read(token, url, fetchImpl) {
  let response;
  try {
    response = await fetchImpl(url, { headers: { authorization: `Bearer ${token}` } });
  } catch {
    throw new GoogleUnavailable('No connection to Google.');
  }

  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (response.status === 401) throw new GoogleAuthExpired(body?.error?.message);
  if (!response.ok) {
    throw new GoogleUnavailable(body?.error?.message || `Google answered ${response.status}.`);
  }
  return body ?? {};
}

/**
 * Every calendar this account can read, following `nextPageToken` rather than
 * trusting the first page.
 *
 * `minAccessRole=reader` leaves out calendars shared as free/busy only: their
 * events cannot be read at all, so including them would mean a permanent row
 * in `failed` for something that is working as intended.
 */
export async function listCalendars(token, fetchImpl = fetch) {
  const calendars = [];
  let pageToken;

  do {
    const query = new URLSearchParams({
      maxResults: PAGE_SIZE,
      minAccessRole: 'reader',
      showDeleted: 'false',
      showHidden: 'true', // hidden in Google's own sidebar is still the student's calendar
    });
    if (pageToken) query.set('pageToken', pageToken);

    const body = await read(token, `${API}/users/me/calendarList?${query}`, fetchImpl);
    for (const item of body.items ?? []) {
      if (!item?.id || item.deleted) continue;
      calendars.push({
        id: item.id,
        // summaryOverride is the name the student typed for a calendar someone
        // else owns, which is the name they will look for on this screen.
        name: item.summaryOverride || item.summary || item.id,
        colour: item.backgroundColor || null,
        primary: Boolean(item.primary),
      });
    }
    pageToken = body.nextPageToken;
  } while (pageToken);

  return calendars;
}

/**
 * The raw events of one calendar inside one window.
 *
 * `singleEvents=true` expands a recurrence into the instances that actually
 * happen. Without it a weekly lecture comes back once, as a rule, and the week
 * looks empty. `orderBy=startTime` is only legal alongside it.
 */
export async function listEvents(token, calendarId, { timeMin, timeMax }, fetchImpl = fetch) {
  const items = [];
  let pageToken;

  do {
    const query = new URLSearchParams({
      timeMin,
      timeMax,
      singleEvents: 'true',
      orderBy: 'startTime',
      showDeleted: 'false',
      maxResults: PAGE_SIZE,
    });
    if (pageToken) query.set('pageToken', pageToken);

    const url = `${API}/calendars/${encodeURIComponent(calendarId)}/events?${query}`;
    const body = await read(token, url, fetchImpl);
    for (const item of body.items ?? []) items.push(item);
    pageToken = body.nextPageToken;
  } while (pageToken);

  return items;
}

/**
 * @returns {{calendars: Array, events: Array, failed: Array<{id, name, reason}>}}
 *
 * Throws only when the whole read is impossible: the token has expired, or the
 * calendar list itself could not be fetched. A calendar that refuses is
 * reported in `failed` and the rest of the day still goes on screen.
 */
export async function loadAllEvents(token, window, deps = {}) {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const calendars = await (deps.listCalendars ?? listCalendars)(token, fetchImpl);
  const readEvents = deps.listEvents ?? listEvents;

  const results = await Promise.all(
    calendars.map(async (calendar) => {
      try {
        return { calendar, raw: await readEvents(token, calendar.id, window, fetchImpl) };
      } catch (error) {
        // Not this calendar's fault, and not survivable by skipping it.
        if (error?.needsConsent) throw error;
        return { calendar, error };
      }
    }),
  );

  const events = [];
  const failed = [];
  for (const { calendar, raw, error } of results) {
    if (error) {
      failed.push({
        id: calendar.id,
        name: calendar.name,
        reason: error.message || 'unknown error',
      });
      continue;
    }
    for (const item of raw) {
      const event = shapeEvent(item, calendar);
      if (event) events.push(event);
    }
  }

  return { calendars, events, failed };
}
